// Per-module download folders using the File System Access API.
// Folder handles are stored in IndexedDB; a global click interceptor routes
// every blob download (a[download] with blob: href) into the folder chosen
// for the current module. No folder chosen => normal browser download.
import { toast } from "sonner";

export const MODULES = [
  { key: "sales_invoice", label: "Sales Invoice" },
  { key: "purchase", label: "Purchase" },
  { key: "bank_reconciliation", label: "Bank Reconciliation" },
  { key: "customers", label: "Customers / Ledger" },
  { key: "godown_stock", label: "Godown / Stock" },
  { key: "gst_reports", label: "GST Reports" },
  { key: "manufacturing", label: "Manufacturing" },
  { key: "other", label: "Other Downloads" },
];

const DB_NAME = "gst_download_paths";
const STORE = "handles";
const handleCache = {}; // key -> FileSystemDirectoryHandle

function idb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

async function idbSet(key, val) {
  const d = await idb();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, "readwrite");
    if (val === null) tx.objectStore(STORE).delete(key);
    else tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

async function idbGetAll() {
  const d = await idb();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, "readonly");
    const out = {};
    const req = tx.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (c) { out[c.key] = c.value; c.continue(); }
      else res(out);
    };
    req.onerror = () => rej(req.error);
  });
}

export function isFolderPickSupported() {
  return typeof window.showDirectoryPicker === "function";
}

export async function loadSavedFolders() {
  try {
    const all = await idbGetAll();
    Object.keys(all).forEach((k) => { handleCache[k] = all[k]; });
    return Object.fromEntries(Object.entries(all).map(([k, h]) => [k, h.name]));
  } catch { return {}; }
}

export function getFolderName(moduleKey) {
  return handleCache[moduleKey]?.name || null;
}

export async function chooseModuleFolder(moduleKey) {
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  handleCache[moduleKey] = handle;
  await idbSet(moduleKey, handle);
  return handle.name;
}

export async function clearModuleFolder(moduleKey) {
  delete handleCache[moduleKey];
  await idbSet(moduleKey, null);
}

async function ensurePermission(handle) {
  const opts = { mode: "readwrite" };
  if ((await handle.queryPermission(opts)) === "granted") return true;
  return (await handle.requestPermission(opts)) === "granted";
}

export async function saveBlobToModule(moduleKey, filename, blob) {
  const handle = handleCache[moduleKey] || handleCache.other;
  if (!handle) return false;
  if (!(await ensurePermission(handle))) return false;
  const fh = await handle.getFileHandle(filename, { create: true });
  const w = await fh.createWritable();
  await w.write(blob);
  await w.close();
  return true;
}

export function moduleForPath(pathname) {
  const p = (pathname || "").toLowerCase();
  if (p.includes("invoice") || p.includes("eway") || p.includes("sales")) return "sales_invoice";
  if (p.includes("purchase")) return "purchase";
  if (p.includes("bank")) return "bank_reconciliation";
  if (p.includes("customer") || p.includes("ledger")) return "customers";
  if (p.includes("godown") || p.includes("stock")) return "godown_stock";
  if (p.includes("gst") || p.includes("schedule") || p.includes("itr") || p.includes("it-")) return "gst_reports";
  if (p.includes("manufactur")) return "manufacturing";
  return "other";
}

let installed = false;
export function installDownloadInterceptor() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  loadSavedFolders();
  // Keep blob: URLs alive long enough for our async re-fetch — pages revoke
  // them immediately after a.click().
  const origRevoke = URL.revokeObjectURL.bind(URL);
  URL.revokeObjectURL = (u) => { setTimeout(() => { try { origRevoke(u); } catch { /* noop */ } }, 15000); };
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest ? e.target.closest("a[download]") : null;
    if (!a || a.dataset.dpBypass) return;
    const href = a.href || "";
    if (!href.startsWith("blob:")) return;
    const moduleKey = moduleForPath(window.location.pathname);
    const handle = handleCache[moduleKey] || handleCache.other;
    if (!handle) return; // no folder configured — default browser download
    e.preventDefault();
    e.stopPropagation();
    const filename = a.getAttribute("download") || `download-${Date.now()}`;
    fetch(href)
      .then((r) => r.blob())
      .then((b) => saveBlobToModule(moduleKey, filename, b))
      .then((ok) => {
        if (!ok) throw new Error("no permission");
        try {
          toast.success(`Saved "${filename}" to folder "${(handleCache[moduleKey] || handleCache.other).name}"`);
        } catch { /* sonner optional */ }
      })
      .catch(() => {
        // Fallback — normal browser download
        const b = document.createElement("a");
        b.href = href; b.download = filename; b.dataset.dpBypass = "1";
        document.body.appendChild(b); b.click(); b.remove();
      });
  }, true);
}
